import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useCart } from "../contexts/CartContext";

// Mock ProductCard component with images
const ProductCard = ({ product }) => (
  <div className="card h-100 shadow-sm product-card">
    <img 
      src={product.image} 
      className="card-img-top" 
      alt={product.name}
      style={{ height: "200px", objectFit: "cover" }}
      loading="lazy"
    />
    <div className="card-body d-flex flex-column">
      <h5 className="card-title">{product.name}</h5>
      <p className="card-text text-muted flex-grow-1">{product.description}</p>
      <div className="mt-auto">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="badge bg-light text-dark">{product.category}</span>
          {product.rating && (
            <div className="text-warning">
              {Array.from({ length: 5 }, (_, i) => (
                <i key={i} className={`bi ${i < product.rating ? 'bi-star-fill' : 'bi-star'}`}></i>
              ))}
              <small className="text-muted ms-1">({product.rating.toFixed(1)})</small>
            </div>
          )}
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <span className="h5 mb-0 text-primary">{product.price.toFixed(2)}€</span>
          {product.hasSubscriptionOption && (
            <small className="text-success">
              <i className="bi bi-arrow-repeat me-1"></i>
              Abonnement
            </small>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Constantes
const CATEGORIES = [
  { id: "all", name: "Toutes les box" },
  { id: "Mensuelle", name: "Box mensuelles" },
  { id: "Édition limitée", name: "Éditions limitées" },
  { id: "Premium", name: "Box premium" },
  { id: "Cadeau", name: "Idées cadeaux" },
];

const SORT_OPTIONS = [
  { value: "featured", label: "En vedette" },
  { value: "newest", label: "Nouveautés" },
  { value: "price-asc", label: "Prix croissant" },
  { value: "price-desc", label: "Prix décroissant" },
  { value: "rating", label: "Mieux notés" },
];

const PURCHASE_TYPES = [
  { value: "all", label: "Tous les types" },
  { value: "single", label: "Achat unique" },
  { value: "subscription", label: "Abonnement" },
];

const RATING_FILTERS = [
  { value: 5, label: "(5 étoiles)", stars: 5 },
  { value: 4, label: "(4+ étoiles)", stars: 4 },
  { value: 3, label: "(3+ étoiles)", stars: 3 },
];

// Styles optimisés
const styles = {
  headerBackground: {
    backgroundColor: "#543ba4",
    color: "white",
    borderRadius: "0",
    padding: "2.5rem 0",
    marginBottom: "2rem",
  },
  sectionTitle: {
    position: "relative",
    marginBottom: "1.5rem",
    fontWeight: "600",
  },
  sectionTitleLine: {
    display: "block",
    width: "60px",
    height: "3px",
    backgroundColor: "#543ba4",
    margin: "0.75rem 0",
    borderRadius: "2px",
  },
  filterCard: {
    border: "none",
    borderRadius: "0.5rem",
    boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
  },
  filterTitle: {
    fontSize: "1.1rem",
    fontWeight: "600",
    marginBottom: "1rem",
  },
  filterGroup: {
    marginBottom: "1.5rem",
  },
  priceRange: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  priceInput: {
    width: "80px",
    textAlign: "center",
    border: "1px solid #ced4da",
    borderRadius: "0.25rem",
    padding: "0.375rem 0.25rem",
    fontSize: "0.875rem",
  },
  categoryTag: {
    backgroundColor: "#f3f0ff",
    color: "#543ba4",
    fontWeight: "500",
    borderRadius: "50rem",
    padding: "0.5rem 1rem",
    fontSize: "0.875rem",
    display: "inline-block",
    marginBottom: "1rem",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "none",
    textDecoration: "none",
  },
  categoryTagActive: {
    backgroundColor: "#543ba4",
    color: "white",
    transform: "translateY(-1px)",
    boxShadow: "0 2px 4px rgba(84, 59, 164, 0.2)",
  },
  breadcrumb: {
    backgroundColor: "#f8f9fa",
    padding: "0.75rem 0",
    marginBottom: "1.5rem",
  },
  primaryButton: {
    backgroundColor: "#543ba4",
    borderColor: "#543ba4",
    color: "white",
  },
  secondaryButton: {
    backgroundColor: "#f89638",
    borderColor: "#f89638",
    color: "white",
  },
  productCard: {
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
  },
};

// Utilitaires
const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

const CategoryPage = () => {
  // Utilisation du CartContext au lieu des données mockées
  const { products } = useCart();
  
  // États du composant
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100 });
  const [sortBy, setSortBy] = useState("featured");
  const [purchaseType, setPurchaseType] = useState("all");
  const [selectedRatings, setSelectedRatings] = useState(new Set([3, 4, 5]));

  // Simulation du chargement initial
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  // Fonction de filtrage optimisée avec useMemo
  const filteredProducts = useMemo(() => {
    if (loading || !products || products.length === 0) return [];

    return products
      .filter((product) => {
        // Filtre par catégorie
        if (activeCategory !== "all") {
          if (product.category !== activeCategory) {
            return false;
          }
        }

        // Filtre par prix
        if (product.price < priceRange.min || product.price > priceRange.max) {
          return false;
        }

        // Filtre par type d'achat
        if (purchaseType !== "all") {
          if (purchaseType === "single" && product.hasSubscriptionOption) return false;
          if (purchaseType === "subscription" && !product.hasSubscriptionOption) return false;
        }

        // Filtre par note
        if (product.rating) {
          const productRating = Math.floor(product.rating);
          const hasMatchingRating = Array.from(selectedRatings).some(rating => {
            if (rating === 5) return productRating === 5;
            if (rating === 4) return productRating >= 4;
            if (rating === 3) return productRating >= 3;
            return false;
          });
          
          if (!hasMatchingRating) return false;
        }

        return true;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "price-asc":
            return a.price - b.price;
          case "price-desc":
            return b.price - a.price;
          case "newest":
            return b.id - a.id; // Supposer que les IDs plus élevés sont plus récents
          case "rating":
            return (b.rating || 0) - (a.rating || 0);
          default:
            return 0; // featured order
        }
      });
  }, [products, loading, activeCategory, priceRange, purchaseType, selectedRatings, sortBy]);

  // Gestionnaires d'événements optimisés avec useCallback
  const handleCategoryChange = useCallback((categoryId) => {
    setActiveCategory(categoryId);
  }, []);

  const handlePriceChange = useCallback((type, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return;

    setPriceRange(prev => {
      if (type === "min") {
        return numValue <= prev.max ? { ...prev, min: numValue } : prev;
      } else {
        return numValue >= prev.min ? { ...prev, max: numValue } : prev;
      }
    });
  }, []);

  const handleRatingToggle = useCallback((rating) => {
    setSelectedRatings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rating)) {
        newSet.delete(rating);
      } else {
        newSet.add(rating);
      }
      return newSet;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setActiveCategory("all");
    setPriceRange({ min: 0, max: 100 });
    setSortBy("featured");
    setPurchaseType("all");
    setSelectedRatings(new Set([3, 4, 5]));
  }, []);

  // Composant StarRating pour réutilisabilité
  const StarRating = useCallback(({ rating, maxRating = 5 }) => (
    <span className="text-warning">
      {Array.from({ length: maxRating }, (_, index) => (
        <i
          key={index}
          className={`bi ${index < rating ? "bi-star-fill" : "bi-star"}`}
        />
      ))}
    </span>
  ), []);

  return (
    <div className="category-page">

      {/* En-tête de la page */}
      <header style={styles.headerBackground}>
        <div className="container text-center">
          <h1 className="display-4 fw-bold">Nos Box</h1>
          <p className="lead mb-0">
            Découvrez notre sélection de box pour tous les goûts et toutes les occasions.
          </p>
        </div>
      </header>

      {/* Sélecteur de catégories */}
      <div className="container mb-4">
        <div className="d-flex flex-wrap justify-content-center gap-2">
          {CATEGORIES.map((category) => (
            <button
              key={category.id}
              type="button"
              className="btn"
              style={{
                ...styles.categoryTag,
                ...(activeCategory === category.id ? styles.categoryTagActive : {}),
              }}
              onClick={() => handleCategoryChange(category.id)}
              aria-pressed={activeCategory === category.id}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu principal */}
      <div className="container py-4">
        <div className="row g-4">
          {/* Filtres et tri */}
          <aside className="col-lg-3">
            <div className="card mb-4" style={styles.filterCard}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="h5 mb-0" style={styles.filterTitle}>
                    Filtres
                  </h2>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={resetFilters}
                  >
                    Réinitialiser
                  </button>
                </div>

                {/* Tri */}
                <div style={styles.filterGroup}>
                  <h3 className="h6 mb-2">Trier par</h3>
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Options de tri"
                  >
                    {SORT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Gamme de prix */}
                <div style={styles.filterGroup}>
                  <h3 className="h6 mb-2">Gamme de prix</h3>
                  <div style={styles.priceRange}>
                    <input
                      type="number"
                      style={styles.priceInput}
                      value={priceRange.min}
                      onChange={(e) => handlePriceChange("min", e.target.value)}
                      min="0"
                      step="0.1"
                      aria-label="Prix minimum"
                    />
                    <span className="text-muted">à</span>
                    <input
                      type="number"
                      style={styles.priceInput}
                      value={priceRange.max}
                      onChange={(e) => handlePriceChange("max", e.target.value)}
                      min={priceRange.min}
                      step="0.1"
                      aria-label="Prix maximum"
                    />
                    <span className="text-muted">€</span>
                  </div>
                </div>

                {/* Type d'achat */}
                <div style={styles.filterGroup}>
                  <h3 className="h6 mb-2">Type d'achat</h3>
                  <div role="radiogroup" aria-labelledby="purchase-type-heading">
                    {PURCHASE_TYPES.map(type => (
                      <div key={type.value} className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="purchaseType"
                          id={`purchaseType${type.value}`}
                          checked={purchaseType === type.value}
                          onChange={() => setPurchaseType(type.value)}
                        />
                        <label className="form-check-label" htmlFor={`purchaseType${type.value}`}>
                          {type.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Note client */}
                <div style={styles.filterGroup}>
                  <h3 className="h6 mb-2">Note client</h3>
                  <div>
                    {RATING_FILTERS.map(rating => (
                      <div key={rating.value} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`rating${rating.value}`}
                          checked={selectedRatings.has(rating.value)}
                          onChange={() => handleRatingToggle(rating.value)}
                        />
                        <label className="form-check-label d-flex align-items-center" htmlFor={`rating${rating.value}`}>
                          <StarRating rating={rating.stars} />
                          <span className="ms-2">{rating.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bannière promotionnelle */}
            <div className="card border-0 bg-light mb-4">
              <div className="card-body p-4 text-center">
                <div className="mb-3" style={{ color: "#f89638", fontSize: "2rem" }}>
                  <i className="bi bi-gift" aria-hidden="true"></i>
                </div>
                <h2 className="h5 mb-3">Offre spéciale</h2>
                <p className="mb-3">-10% sur votre première commande avec le code</p>
                <div className="bg-white p-2 mb-3 rounded" style={{ color: "#543ba4", fontWeight: "bold" }}>
                  BIENVENUE
                </div>
                <button type="button" className="btn w-100" style={styles.secondaryButton}>
                  En profiter
                </button>
              </div>
            </div>

            {/* Service client */}
            <div className="card border-0" style={styles.filterCard}>
              <div className="card-body p-4">
                <div className="text-center mb-3" style={{ color: "#543ba4", fontSize: "2rem" }}>
                  <i className="bi bi-headset" aria-hidden="true"></i>
                </div>
                <h2 className="h5 text-center mb-3">Besoin d'aide ?</h2>
                <p className="text-center small mb-3">
                  Notre équipe est à votre disposition pour répondre à toutes vos questions.
                </p>
                <div className="d-grid gap-2">
                  <a href="tel:+32123456789" className="btn btn-outline-primary">
                    <i className="bi bi-telephone me-2" aria-hidden="true"></i>
                    +32 123 456 789
                  </a>
                  <a href="mailto:contact@boxplaisir.be" className="btn btn-outline-primary">
                    <i className="bi bi-envelope me-2" aria-hidden="true"></i>
                    contact@boxplaisir.be
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* Liste des produits */}
          <main className="col-lg-9">
            {/* En-tête avec résultats */}
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h1 style={styles.sectionTitle}>
                  {activeCategory === "all" ? "Tous nos produits" : CATEGORIES.find(cat => cat.id === activeCategory)?.name || "Produits"}
                  <div style={styles.sectionTitleLine}></div>
                </h1>
                <p className="text-muted">
                  {filteredProducts.length} produit{filteredProducts.length !== 1 ? "s" : ""} trouvé{filteredProducts.length !== 1 ? "s" : ""}
                </p>
              </div>
              
              {/* Tri mobile */}
              <div className="d-lg-none">
                <select
                  className="form-select form-select-sm"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Options de tri mobile"
                >
                  {SORT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contenu conditionnel */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" style={{ color: "#543ba4" }} role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3">Chargement des produits...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-search display-1 text-muted" aria-hidden="true"></i>
                </div>
                <h2 className="h4">Aucun produit trouvé</h2>
                <p className="text-muted mb-4">
                  Essayez de modifier vos filtres ou de réinitialiser la recherche.
                </p>
                <button type="button" className="btn btn-primary" onClick={resetFilters}>
                  Réinitialiser les filtres
                </button>
              </div>
            ) : (
              <>
                {/* Grille de produits */}
                <div className="row g-4">
                  {filteredProducts.map((product) => (
                    <div key={product.id} className="col-md-6 col-xl-4">
                      <a
                        href={`/product/${product.id}`}
                        className="text-decoration-none text-reset"
                        aria-label={`Voir les détails de ${product.name}`}
                        style={styles.productCard}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <ProductCard product={product} />
                      </a>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {filteredProducts.length > 12 && (
                  <nav className="d-flex justify-content-center mt-5" aria-label="Navigation des pages">
                    <ul className="pagination">
                      <li className="page-item disabled">
                        <span className="page-link" aria-label="Page précédente">
                          <i className="bi bi-chevron-left" aria-hidden="true"></i>
                        </span>
                      </li>
                      <li className="page-item active" aria-current="page">
                        <span className="page-link" style={styles.primaryButton}>
                          1
                        </span>
                      </li>
                      <li className="page-item">
                        <button type="button" className="page-link" style={{ color: "#543ba4" }}>
                          2
                        </button>
                      </li>
                      <li className="page-item">
                        <button type="button" className="page-link" style={{ color: "#543ba4" }}>
                          3
                        </button>
                      </li>
                      <li className="page-item">
                        <button type="button" className="page-link" aria-label="Page suivante">
                          <i className="bi bi-chevron-right" aria-hidden="true"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Sections promotionnelles */}
      <section className="py-5 bg-light">
        <div className="container py-4">
          <div className="row g-4">
            <div className="col-md-6">
              <div className="card border-0 h-100 shadow-sm">
                <div className="card-body p-4 text-center">
                  <div className="mb-3" style={{ color: "#543ba4", fontSize: "2.5rem" }}>
                    <i className="bi bi-bag" aria-hidden="true"></i>
                  </div>
                  <h2 className="h4 mb-3">Achat unique</h2>
                  <p className="text-muted mb-4">
                    Commandez une box sans engagement. Idéal pour essayer nos produits ou offrir un cadeau.
                  </p>
                  <a href="/category" className="btn btn-primary w-100">
                    Découvrir nos box
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 h-100 shadow-sm">
                <div className="card-body p-4 text-center">
                  <div className="position-relative mb-3">
                    <i className="bi bi-calendar-check" style={{ color: "#543ba4", fontSize: "2.5rem" }} aria-hidden="true"></i>
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill" style={{ backgroundColor: "#f89638" }}>
                      -15%
                    </span>
                  </div>
                  <h2 className="h4 mb-3">Option abonnement</h2>
                  <p className="text-muted mb-4">
                    Économisez 15% en choisissant l'option abonnement lors de votre achat. Sans engagement et résiliable à tout moment.
                  </p>
                  <a href="/subscription" className="btn btn-outline-primary w-100">
                    En savoir plus
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-5 text-center" style={{ backgroundColor: "#543ba4", color: "white" }}>
        <div className="container py-4">
          <h2 className="mb-4">Restez informé</h2>
          <p className="lead mb-4 mx-auto" style={{ maxWidth: "700px" }}>
            Inscrivez-vous à notre newsletter pour être le premier à découvrir nos nouvelles box et offres exclusives.
          </p>
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="input-group">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Votre adresse email"
                  aria-label="Adresse email pour la newsletter"
                />
                <button className="btn px-4" style={styles.secondaryButton} type="button">
                  S'inscrire
                </button>
              </div>
              <div className="form-text text-white-50 mt-2">
                En vous inscrivant, vous acceptez de recevoir nos communications marketing.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;